import type { RoutedKey } from "../../ui/input/router.ts";
import { pageKeys, type KeyTable } from "../controls/page.ts";

/**
 * Jump's key hops (ADR-116 decisions 17 and 18; ADR-061's amendment for P1.9-T174, point 3): the key's listener for
 * the input router, which hears a key only while the game owns the keyboard, so its key in a text field types and never
 * hops. Jump is in the page's key table, on Space unless the player chooses another; a press whose target is a button,
 * a link, a box or a list row is left to the control, which Space and Enter press. While the character is dead the key is left to the death screen, whose
 * Respawn it presses.
 */

/**
 * The controls a focused Space presses, and anything inside one.
 */
const CONTROL = "button, a[href], input, select, summary, [role='button'], [role='checkbox'], [role='radio'], [role='switch'], [role='tab'], [role='menuitem'], [role='option'], [role='row']";

/**
 * What the listener asks of the page.
 */
export interface JumpKeyOptions {
  /**
   * Plays the own character's hop and asks the next input for the jump bit (`LocalPlayer.hop`).
   *
   * @returns True when it played.
   */
  hop(): boolean;
  /**
   * Whether the character is dead, so the key is the death screen's.
   *
   * @returns True while dead.
   */
  dead(): boolean;
  /**
   * Whether a Game Master's freeze holds the character, its grace included, in which it may not hop.
   *
   * @returns True while held.
   */
  held(): boolean;
  /** The key table, which names Jump's key; the page's unless given. */
  readonly keys?: Pick<KeyTable, "actionOf">;
}

/**
 * Whether an event's target is a control a focused Space presses.
 *
 * @param target What had focus: an element, or anything else, which is no control.
 * @returns True for a button, a link, a box, a tab, a menu item, an option or a row, or anything inside one.
 */
export function isPressedBySpace(target: unknown): boolean {
  const closest = (target as { closest?: unknown } | null)?.closest;
  return typeof closest === "function" && (target as Pick<Element, "closest">).closest(CONTROL) !== null;
}

/**
 * Makes Space's listener. A press of Space the game owns, outside a control and while the character lives, is
 * consumed; a fresh one hops unless a freeze holds the character, and an auto-repeat never does, so a held Space hops
 * once. The page's own refusals are `LocalPlayer.hop`'s.
 *
 * @param options What the listener asks of the page.
 * @returns The listener.
 * @example
 * input.onKey(jumpKeys({ hop: () => local.hop(now(), waitMs), dead: () => local.dead, held: () => frozen() }));
 */
export function jumpKeys(options: JumpKeyOptions): (event: RoutedKey) => void {
  return (event) => {
    if ((options.keys ?? pageKeys).actionOf(event) !== "jump" || event.defaultPrevented || isPressedBySpace(event.target) || options.dead()) {
      return;
    }
    event.preventDefault();
    if (!event.repeat && !options.held()) {
      options.hop();
    }
  };
}
